export const GIFT_CARD_SEND_FORM_CACHE_KEY = 'bookkeeper:gift-card-send-form'
export const GIFT_CARD_SEND_FORM_CACHE_TTL_MS = 4 * 60 * 60 * 1000

export interface GiftCardSendFormDraft {
  amount: string
  recipientEmail: string
  recipientName: string
  cardNumber: string
  senderName: string
  message: string
}

interface StoredGiftCardSendFormDraft extends GiftCardSendFormDraft {
  savedAt: number
}

function isDraftShape(value: unknown): value is GiftCardSendFormDraft {
  if (value == null || typeof value !== 'object') return false
  const draft = value as Record<string, unknown>
  return (
    typeof draft.amount === 'string' &&
    typeof draft.recipientEmail === 'string' &&
    typeof draft.recipientName === 'string' &&
    typeof draft.cardNumber === 'string' &&
    typeof draft.senderName === 'string' &&
    typeof draft.message === 'string'
  )
}

export function loadGiftCardSendFormDraft(): GiftCardSendFormDraft | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(GIFT_CARD_SEND_FORM_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredGiftCardSendFormDraft>
    const savedAt = (parsed as Partial<StoredGiftCardSendFormDraft>).savedAt
    if (!isDraftShape(parsed) || typeof savedAt !== 'number') {
      localStorage.removeItem(GIFT_CARD_SEND_FORM_CACHE_KEY)
      return null
    }
    if (Date.now() - savedAt >= GIFT_CARD_SEND_FORM_CACHE_TTL_MS) {
      localStorage.removeItem(GIFT_CARD_SEND_FORM_CACHE_KEY)
      return null
    }
    return {
      amount: parsed.amount,
      recipientEmail: parsed.recipientEmail,
      recipientName: parsed.recipientName,
      cardNumber: parsed.cardNumber,
      senderName: parsed.senderName,
      message: parsed.message,
    }
  } catch {
    try {
      localStorage.removeItem(GIFT_CARD_SEND_FORM_CACHE_KEY)
    } catch {
      // Ignore storage errors (privacy mode, SSR, test env).
    }
    return null
  }
}

export function saveGiftCardSendFormDraft(draft: GiftCardSendFormDraft): void {
  try {
    if (typeof localStorage === 'undefined') return
    const payload: StoredGiftCardSendFormDraft = {
      ...draft,
      savedAt: Date.now(),
    }
    localStorage.setItem(GIFT_CARD_SEND_FORM_CACHE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore quota / privacy errors.
  }
}

export function clearGiftCardSendFormDraft(): void {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.removeItem(GIFT_CARD_SEND_FORM_CACHE_KEY)
  } catch {
    // Ignore storage errors.
  }
}
